// =============================================================================
// Optim TURPE V2 — CE QUE LE DIAGNOSTIC DE LA COURBE DIT, AU BON NIVEAU
// (DEV-2182, revue M4 et M11 du 25/09/2026, courtage#256).
//
// Ce que le client doit vivre : sous « le dénivelé proposé couvre 100 % du
// temps », aucun mois de dépassement ne s'affiche quand la pointe ne dépasse
// jamais le proposé. Les mois et la part d'hiver qui décrivaient le niveau
// ALTERNATIF se lisent sous « si vous souscriviez N ». Une plage motrice n'est
// jamais désignée sur une égalité.
//
// Les clés lues :
// - `driver.mois_concernes_depassement_propose` et
//   `driver.part_hiver_depassement_propose_pct` : le dépassement du PROPOSÉ,
//   clés additives de courtage#256. Vides ou `null` : rien à dire.
// - `arbitrage.depassement_si_alternatif.mois_concernes` et `.part_hiver_pct` :
//   le niveau alternatif. Sur une étude antérieure, la part d'hiver du niveau
//   alternatif n'était servie que sous `driver.part_hiver_depassement_pct`,
//   qui reste le repli tant que les clés neuves sont absentes.
// - `synthese.plage_pilote` (`null` sur une égalité) et `synthese.plages_a_egalite`.
// =============================================================================

import {
    formatNombreFr,
    formatPuissanceFr,
    nomsDesMois,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    LIBELLE_PART_DECEMBRE_FEVRIER,
    libelleDureeHeures,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageDiagnostic,
    OptimTurpeV2PlageKey,
} from "@modules/courtage/types/optim-turpe-v2"

function nombre(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** Le niveau alternatif nommé comme ce qu'il est : un repère (DEV-2217). */
const NIVEAU_INDICATIF = "Niveau indicatif, dépassé au plus 1\u00a0% du temps"

function pct(valeur: number): string {
    return `${formatNombreFr(valeur, 1)}\u00a0%`
}

/**
 * « Dépassement du dénivelé proposé : 3,8 h, en janvier, février ; part de
 * décembre à février 100 % », ou `null` quand les clés neuves ne disent aucun
 * dépassement du proposé. La durée (`driver.depassement_du_propose.duree_h`)
 * est celle du scénario proposé, jamais celle du niveau indicatif (DEV-2217).
 * Les anciennes clés ne sont JAMAIS lues ici : elles décrivaient le niveau
 * alternatif.
 */
export function lireDepassementDuPropose(
    diag: OptimTurpeV2PlageDiagnostic | null | undefined,
    structureChoisie = false
): string | null {
    const driver = diag?.driver
    if (!driver) return null
    const mois = nomsDesMois(driver.mois_concernes_depassement_propose ?? [])
    if (!mois) return null
    const duree = libelleDureeHeures(driver.depassement_du_propose?.duree_h)
    const denivele = structureChoisie ? "saisie" : "recommandée"
    const debut = duree
        ? `Dépassement de la puissance souscrite ${denivele} : ${duree}, en ${mois}`
        : `Dépassement de la puissance souscrite ${denivele} : ${mois}`
    const hiver = nombre(driver.part_hiver_depassement_propose_pct)
    return hiver === null
        ? debut
        : `${debut} ; part de décembre à février ${pct(hiver)}`
}

/**
 * « Niveau indicatif, dépassé au plus 1 % du temps : si vous souscriviez
 * 55 kVA, dépassement pendant 5 h, en avril, mai ; part de décembre à février
 * du dépassement : 0 % ». `null` sans niveau alternatif servi.
 *
 * Le niveau alternatif est le 99ᵉ centile de la plage arrondi au kVA supérieur
 * (`quantile_alternatif: 0.99`, `diagnostic_courbe.py`) : un repère, pas le
 * scénario « Assumer les dépassements » (DEV-2217).
 */
export function lireSiAlternatif(
    diag: OptimTurpeV2PlageDiagnostic | null | undefined,
    unite: string
): string | null {
    const arbitrage = diag?.arbitrage
    const niveau = nombre(arbitrage?.niveau_alternatif_kva)
    if (!arbitrage || niveau === null) return null
    const alt = arbitrage.depassement_si_alternatif ?? {}
    const debut = `${NIVEAU_INDICATIF} : si vous souscriviez ${formatPuissanceFr(niveau, unite)}`
    const nPoints = nombre(alt.n_points)
    if (nPoints === 0) return `${debut}, aucun dépassement mesuré`
    const morceaux: string[] = []
    const duree = nombre(alt.duree_h)
    if (duree !== null) {
        morceaux.push(`dépassement pendant ${formatNombreFr(duree, 1)}\u00a0h`)
    }
    const mois = nomsDesMois(alt.mois_concernes ?? [])
    if (mois) morceaux.push(`en ${mois}`)
    let texte = morceaux.length > 0 ? `${debut}, ${morceaux.join(", ")}` : debut
    // La part d'hiver du niveau alternatif : sa clé neuve, sinon, sur une étude
    // antérieure à courtage#256 (aucune clé neuve servie), l'ancienne clé du
    // driver, qui la portait.
    const driver = diag?.driver
    const etudeAnterieure =
        alt.part_hiver_pct === undefined &&
        driver?.mois_concernes_depassement_propose === undefined &&
        driver?.part_hiver_depassement_propose_pct === undefined
    const hiver =
        nombre(alt.part_hiver_pct) ??
        (etudeAnterieure ? nombre(driver?.part_hiver_depassement_pct) : null)
    if (hiver !== null) {
        texte += ` ; ${LIBELLE_PART_DECEMBRE_FEVRIER} : ${pct(hiver)}`
    }
    return texte
}

/**
 * La plage motrice est-elle INDÉTERMINÉE ? Le moteur le dit par
 * `synthese.plage_pilote: null` sur une égalité (courtage#256). Rend les
 * classes à égalité, ou `null` quand la plage est désignée (ou non servie).
 */
export function plagesMotricesAEgalite(
    etude: OptimTurpeV2Etude
): OptimTurpeV2PlageKey[] | null {
    const synthese = etude.diagnostic?.synthese
    if (!synthese || synthese.plage_pilote !== null) return null
    const egalite = synthese.plages_a_egalite
    return Array.isArray(egalite) && egalite.length > 0 ? egalite : null
}

/** « Plage qui fixe la puissance : HPB et HPH à égalité ». */
export function libellePlageMotriceIndeterminee(
    egalite: OptimTurpeV2PlageKey[]
): string {
    const liste =
        egalite.length <= 1
            ? egalite.join("")
            : `${egalite.slice(0, -1).join(", ")} et ${egalite[egalite.length - 1]}`
    return `Plage qui fixe la puissance : ${liste} à égalité`
}

/** Le message de synthèse, servi TEL QUEL par le moteur, ou `null`. */
export function messageDeSynthese(etude: OptimTurpeV2Etude): string | null {
    const message = etude.diagnostic?.synthese?.message
    return typeof message === "string" && message.trim() !== "" ? message : null
}
